import React, { useState } from "react";
import "./Dummy.css";
import {
  ArrowLeft,
  Package,
  User,
  Store,
  Calendar,
  Clock,
  FileText,
  Download,
  AlertTriangle,
  Truck,
  CreditCard,
  Tag,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  MapPin,
  Eye,
  EyeOff,
  Copy,
  Check,
} from "lucide-react";

// Utility functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount);
};

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Dummy = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [showIds, setShowIds] = useState(false);
  const [copiedId, setCopiedId] = useState("");

  // Mock order data - moved directly into component
  const orderData = {
    order_details: {
      order_status: "delivered",
      settlement_status: "pending",
      order_created_at: "2024-01-15T10:30:00Z",
      order_delivered_at: "2024-01-20T14:22:00Z",
      settlement_updated_at: "2024-01-20T15:00:00Z",
      domain: "Electronics",
      return_window: 7,
      return_expires_at: "2024-01-27T14:22:00Z",
    },
    buyer_details: {
      buyer_order_id: "BO-2024-001234",
      order_transaction_id: "TXN-789456123",
      buyer_app_id: "BUYER_APP_001",
    },
    seller_details: {
      seller_order_id: "SO-2024-005678",
      settlement_app_id: "SETTLE_APP_001",
      seller_app_id: "SELLER_APP_001",
    },
    order_summary: {
      products: [
        {
          item_name: "Wireless Bluetooth Headphones",
          quantity: 1,
          price: 2999,
        },
        {
          item_name: "USB-C Charging Cable",
          quantity: 2,
          price: 399,
        },
      ],
      subtotal: 3797,
      gst: 683,
      buyer_finder_fee: 150,
      delivery_charges: 100,
      total_order_amount: 4730,
      amount_to_be_settle: 4230,
      settled_amount: 0,
      settlement_breakdown: [
        {
          settlement_id: "SETTLE-0001",
          req_date: "2024-01-30T12:00:00Z",
          settled_date: "2024-01-30T12:00:00Z",
          amount_settled: 2000,
          status: "completed",
          
        },
        {
          settlement_id: "SETTLE-0002",
          req_date: "2024-01-30T12:00:00Z",
          settled_date: "2024-01-30T12:00:00Z",
          amount_settled: 1230,
          status: "pending",
          
        },
      ],
    },
  };

  // Helper functions - moved directly into component
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
      case "completed":
        return <CheckCircle size={12} />;
      case "pending":
      case "processing":
        return <AlertCircle size={12} />;
      case "shipped":
        return <Truck size={12} />;
      case "cancelled":
      case "failed":
        return <XCircle size={12} />;
      default:
        return <AlertCircle size={12} />;
    }
  };

  const calculateDaysRemaining = () => {
    const today = new Date();
    const returnDate = new Date(orderData.order_details.return_expires_at);
    const diffTime = returnDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const isReturnExpired = () => {
    return calculateDaysRemaining() <= 0;
  };

  const handleRequestSettlement = () => {
 
  };

  const handleBack = () => {
    window.history.back();
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(""), 2000);
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
      case "completed":
        return "status-delivered";
      case "pending":
      case "processing":
        return "status-pending";
      case "shipped":
        return "status-shipped";
      case "cancelled":
      case "failed":
        return "status-cancelled";
      default:
        return "bg-light text-muted border-secondary";
    }
  };

  const { order_details, buyer_details, seller_details, order_summary } =
    orderData;

  return (
    <div className="min-vh-100 bg-gradient-custom">
      {/* Floating Header */}
      <div className="sticky-top glass-header shadow-sm">
        <div className="container-fluid" style={{ maxWidth: "1200px" }}>
          <div className="d-flex align-items-center justify-content-between py-2">
            <div className="d-flex align-items-center">
              <button
                onClick={handleBack}
                className="btn btn-glass me-3 d-flex align-items-center"
              >
                <ArrowLeft size={16} className="me-2" />
                <span className="d-none d-sm-inline">Back</span>
              </button>
              <div>
                <h1 className="h5 fw-bold text-dark mb-0">
                  Order #{buyer_details?.buyer_order_id?.slice(-6)}
                </h1>
                <p className="small text-muted mb-0">Order Management</p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-glass d-none d-sm-flex align-items-center">
                <Download size={14} className="me-2" />
                <span>Invoice</span>
              </button>
              <button
                onClick={handleRequestSettlement}
                className="btn d-flex align-items-center px-3 py-2 rounded-3 border-0"
                style={{
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.3s ease",
                }}
              >
                <CreditCard size={14} className="me-2" />
                <span>Request Settlement</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid py-4" style={{ maxWidth: "1200px" }}>
        {/* Status Overview Bar */}
        <div className="glass-effect rounded-4 p-2 mb-4 shadow-sm">
          <div className="row g-3">
            <div className="col-6 col-md-3 text-center">
              <div
                className={`badge d-inline-flex align-items-center gap-1 ${getStatusClass(
                  order_details.order_status
                )}`}
              >
                {getStatusIcon(order_details.order_status)}
                <span className="text-capitalize">
                  {order_details.order_status}
                </span>
              </div>
              <p className="small text-muted mt-1 mb-0">Order Status</p>
            </div>
            <div className="col-6 col-md-3 text-center">
              <div
                className={`badge d-inline-flex align-items-center gap-1 ${getStatusClass(
                  order_details.settlement_status
                )}`}
              >
                {getStatusIcon(order_details.settlement_status)}
                <span className="text-capitalize">
                  {order_details.settlement_status}
                </span>
              </div>
              <p className="small text-muted mt-1 mb-0">Settlement</p>
            </div>
            <div className="col-6 col-md-3 text-center">
              <div className="h5 fw-bold text-dark mb-0">
                {formatCurrency(order_summary.total_order_amount)}
              </div>
              <p className="small text-muted mb-0">Total Amount</p>
            </div>
            <div className="col-6 col-md-3 text-center">
              <div className="h5 fw-bold text-success mb-0">
                {formatCurrency(order_summary.amount_to_be_settle)}
              </div>
              <p className="small text-muted mb-0">To Settle</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-4">
          <div className="glass-effect rounded-4 p-1">
            <div className="row g-1">
              {[
                { id: "overview", label: "Overview", icon: Package },
                { id: "details", label: "Details", icon: FileText },
                { id: "timeline", label: "Timeline", icon: Clock },
              ].map((tab) => (
                <div key={tab.id} className="col-4">
                <button
  onClick={() => setActiveTab(tab.id)}
  className={`btn w-100 d-flex align-items-center justify-content-center gap-2 py-2 px-3 rounded-3 fw-semibold shadow-sm transition ${
    activeTab === tab.id
      ? "bg-primary text-white"
      : "bg-light text-dark bg-opacity-50"
  }`}
  style={{
    border: "1px solid #dee2e6",
    transition: "all 0.2s ease-in-out",
  }}
>
  <tab.icon size={16} />
  <span>{tab.label}</span>
</button>

                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="row g-4">
          {/* Main Content */}
          <div className="col-lg-8">
            {activeTab === "overview" && (
              <div className="row g-4">
                {/* Order Items */}

                <div className="col-12">
                  <div className="glass-effect rounded-4 p-4 shadow-sm">
                    <h3 className="h5 fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Package className="me-2 text-primary" size={20} />
                      Order Items
                    </h3>

                    {/* Scrollable vertical section with custom class */}
                    <div
                      className="scroll-thin"
                      style={{ maxHeight: "150px", overflowY: "auto" }}
                    >
                      {order_summary?.products?.map((item, index) => (
                        <div key={index} className="mb-3">
                          <div className="d-flex align-items-center justify-content-between p-3 bg-light bg-opacity-50 rounded-3">
                            <div className="flex-grow-1">
                              <h6 className="fw-medium text-dark mb-1">
                                {item.item_name}
                              </h6>
                              <p className="small text-muted mb-0">
                                Qty: {item.quantity} ×{" "}
                                {formatCurrency(item.price)}
                              </p>
                            </div>
                            <div className="fw-semibold text-dark">
                              {formatCurrency(item.quantity * item.price)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Return Information */}
                <div className="col-12">
                  <div className="glass-effect rounded-4 p-4 shadow-sm">
                    <h3 className="h5 fw-semibold text-dark mb-2">
                      Return Policy
                    </h3>
                    <div className="row g-3">
                      <div className="col-sm-6">
                        <div className="bg-light bg-opacity-50 rounded-3">
                          <div className="h3 fw-bold text-dark mb-0">
                            {order_details.return_window}
                          </div>
                          <p className="small text-muted mb-0">Days Window</p>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="bg-light bg-opacity-50 rounded-3">
                          <div
                            className={`small fw-medium ${
                              isReturnExpired() ? "text-danger" : "text-success"
                            }`}
                          >
                            {isReturnExpired()
                              ? "Expired"
                              : `${calculateDaysRemaining()} days left`}
                          </div>
                          <p className="small text-muted mb-0">
                            {formatDate(order_details.return_expires_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="glass-effect rounded-4 p-4 shadow-sm">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h3 className="h5 fw-semibold text-dark mb-0 d-flex align-items-center">
                        <User className="me-2 text-primary" size={20} />
                        Buyer App
                      </h3>
                    </div>
                    <div className="row g-3">
                      {Object.entries(buyer_details).map(([key, value]) => (
                        <div key={key} className="col-12">
                          <div className="d-flex align-items-center justify-content-between p-3 bg-light bg-opacity-50 rounded-3">
                            <div className="flex-grow-1">
                              <p
                                className="small fw-medium text-muted text-uppercase mb-1"
                                style={{ letterSpacing: "0.5px" }}
                              >
                                {key.replace(/_/g, " ")}
                              </p>
                              <p className="small font-monospace text-dark mb-0">
                                {value}
                              </p>
                            </div>

                            <button
                              onClick={() => copyToClipboard(value, key)}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              {copiedId === key ? (
                                <Check size={14} className="text-success" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="glass-effect rounded-4 p-4 shadow-sm">
                    <h3 className="h5 fw-semibold text-dark mb-3 d-flex align-items-center">
                      <Store className="me-2 text-primary" size={20} />
                      Seller App
                    </h3>
                    <div className="row g-3">
                      {Object.entries(seller_details).map(([key, value]) => (
                        <div key={key} className="col-12">
                          <div className="d-flex align-items-center justify-content-between p-3 bg-light bg-opacity-50 rounded-3">
                            <div className="flex-grow-1">
                              <p
                                className="small fw-medium text-muted text-uppercase mb-1"
                                style={{ letterSpacing: "0.5px" }}
                              >
                                {key.replace(/_/g, " ")}
                              </p>
                              <p className="small font-monospace text-dark mb-0">
                                {value}
                              </p>
                            </div>
                            <button
                              onClick={() => copyToClipboard(value, key)}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              {copiedId === key ? (
                                <Check size={14} className="text-success" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "timeline" && (
              <div className="glass-effect rounded-4 p-4 shadow-sm">
                <h3 className="h5 fw-semibold text-dark mb-3 d-flex align-items-center">
                  <Clock className="me-2 text-primary" size={20} />
                  Order Timeline
                </h3>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="progress" style={{ height: "10px" }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{
                        width:
                          order_details.order_status === "delivered"
                            ? "100%"
                            : order_details.order_status === "shipped"
                            ? "75%"
                            : order_details.order_status === "processing"
                            ? "50%"
                            : "25%",
                      }}
                    />
                  </div>
                  <div className="d-flex justify-content-between small text-muted mt-1">
                    <span>Created</span>
                    <span>Processing</span>
                    <span>Shipped</span>
                    <span>Delivered</span>
                  </div>
                </div>

                {/* Timeline Steps */}
                <div className="row g-4">
                  {/* Order Created */}
                  <div className="col-12">
                    <div className="d-flex align-items-start gap-3">
                      <div className="timeline-icon bg-primary text-white rounded-circle p-2">
                        <Truck size={16} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-medium text-dark mb-1">
                          Order Created
                        </h6>
                        <p className="small text-muted mb-0">
                          {formatDateTime(order_details.order_created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Order Delivered */}
                  <div className="col-12">
                    <div className="d-flex align-items-start gap-3">
                      <div className="timeline-icon bg-success text-white rounded-circle p-2">
                        <CheckCircle size={16} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-medium text-dark mb-1">
                          Order Delivered
                        </h6>
                        <p className="small text-muted mb-0">
                          {formatDateTime(order_details.order_delivered_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Settlement Updated */}
                  <div className="col-12">
                    <div className="d-flex align-items-start gap-3">
                      <div className="timeline-icon bg-warning text-white rounded-circle p-2">
                        <Clock size={16} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-medium text-dark mb-1">
                          Settlement Updated
                        </h6>
                        <p className="small text-muted mb-0">
                          {formatDateTime(order_details.settlement_updated_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Return Window (Optional Info) */}
                  <div className="col-12">
                    <div className="d-flex align-items-start gap-3">
                      <div className="timeline-icon bg-info text-white rounded-circle p-2">
                        <Clock size={16} />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-medium text-dark mb-1">
                          Return Window Ends
                        </h6>
                        <p className="small text-muted mb-0">
                          {formatDateTime(order_details.return_expires_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Financial Summary */}
          <div className="col-lg-4 mb-4">
            <div className="glass-effect rounded-4 p-4 shadow-sm sticky-sidebar">
              <h3 className="h5 fw-semibold text-dark mb-3 d-flex align-items-center">
                <FileText className="me-2 text-primary" size={20} />
                Order Summary
              </h3>

              <div className="mb-1">
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">Subtotal</span>
                  <span className="fw-medium">
                    {formatCurrency(order_summary.subtotal)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">GST</span>
                  <span className="fw-medium">
                    {formatCurrency(order_summary.gst)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">Finder Fee</span>
                  <span className="fw-medium">
                    {formatCurrency(order_summary.buyer_finder_fee)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">Delivery</span>
                  <span className="fw-medium">
                    {formatCurrency(order_summary.delivery_charges)}
                  </span>
                </div>
                <br />
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold text-dark">Total</span>
                  <span className="fw-bold h5 mb-0">
                    {formatCurrency(order_summary.total_order_amount)}
                  </span>
                </div>
              </div>

              <div className="financial-gradient rounded-3 p-3 mb-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small fw-medium text-success">
                    To Be Settled
                  </span>
                  <span className="fw-bold text-success">
                    {formatCurrency(order_summary.amount_to_be_settle)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small fw-medium text-primary">
                    Settled Amount
                  </span>
                  <span className="fw-bold text-primary">
                    {formatCurrency(order_summary.settled_amount)}
                  </span>
                </div>
              </div>

             
            </div>
          </div>
        </div>

       <div className="glass-effect rounded-4 p-3 mb-4">
  <h5 className="mb-3">Settlement Breakdown</h5>

  {/* 👇 Summary row */}
  <div className="d-flex flex-wrap gap-3 mb-3 px-1 small text-muted">
    <div className="me-3">
      <strong className="text-dark">Amount to be Settle:</strong> ₹{orderData.order_summary.amount_to_be_settle.toLocaleString()}
    </div>
    <div className="me-3">
      <strong className="text-dark">Settled Amount:</strong> ₹{orderData.order_summary.settled_amount.toLocaleString()}
    </div>
    <div>
      <strong className="text-dark">Overall Status:</strong>{" "}
      <span
        className={`badge px-2 py-1 ${
          orderData.order_details.settlement_status === "completed"
            ? "bg-success"
            : orderData.order_details.settlement_status === "pending"
            ? "bg-warning text-dark"
            : "bg-secondary"
        }`}
      >
        {orderData.order_details.settlement_status}
      </span>
    </div>
  </div>

  {/* 👇 Table */}
  <div className="table-responsive">
    <table className="table table-sm table-bordered align-middle mb-0">
      <thead className="table-light">
        <tr>
          <th>Settlement ID</th>
          <th>Settlement Amount</th>
          <th>Status</th>
          <th>Requested Date</th>
          <th>Settled Date</th>
        </tr>
      </thead>
      <tbody>
        {orderData?.order_summary?.settlement_breakdown?.map((entry, index) => (
          <tr key={index}>
            <td>{entry.settlement_id}</td>
            <td>₹{entry.amount_settled.toLocaleString()}</td>
            <td>
              <span
                className={`badge px-2 py-1 ${
                  entry.status === "completed"
                    ? "bg-success text-white"
                    : entry.status === "pending"
                    ? "bg-warning text-dark"
                    : "bg-secondary"
                }`}
              >
                {entry.status}
              </span>
            </td>
            <td>
              {new Date(entry.req_date).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </td>
            <td>
              {new Date(entry.settled_date).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

      </div>
    </div>
  );
};

export default Dummy;
